/**
 * The Courses step of `/welcome` (#648, RegCourses): one row per course on its own tint, the Shown tick and the colour
 * swatches. It sends the same PATCH as `/settings/courses` (`useCourseUpdates`). Course titles are Moodle text,
 * untrusted: plain text with `dir="auto"`. A colour is a label, never a status.
 */

import { Link } from 'react-router';
import { COURSE_PALETTE } from '@davinci/contracts';
import { AuthStage, SignupSteps } from '../../app/auth-layout.js';
import { FailureNotice } from '../../app/failure-notice.js';
import { useLocale, useMessages } from '../../i18n/locale.js';
import { courseShortName, courseTitle } from '../../app/course-names.js';
import { defineMessages } from '../../i18n/messages.js';
import { ConnectedAsRow } from '../settings/moodle-connector.js';
import { courseMessages, formatDue, useCourseUpdates } from '../settings/courses-page.js';

const messages = defineMessages({
  en: { h1: 'Your courses', onTheirWay: 'Your courses are on their way', hint: 'Colours are just labels. Change them any time.', cta: 'Go to Home' },
  he: { h1: 'הקורסים שלך', onTheirWay: 'הקורסים בדרך', hint: 'הצבעים הם רק סימון. אפשר לשנות בכל רגע.', cta: 'לדף הבית' },
});

export function WelcomeCourses(): React.JSX.Element {
  const t = useMessages(messages);
  const c = useMessages(courseMessages);
  const { locale } = useLocale();
  const { courses, mutation, failed, change, waiting } = useCourseUpdates({ pollWhileEmpty: true });

  return (
    <AuthStage glyph="🎓">
      <SignupSteps current="courses" />
      <h1>{t.h1}</h1>
      <ConnectedAsRow variant="welcome" />

      {courses.isPending ? <p role="status">{c.loading}</p> : null}
      {courses.isError ? <FailureNotice error={courses.error} onRetry={() => void courses.refetch()} /> : null}
      {failed !== undefined ? <FailureNotice error={failed.error} onRetry={() => mutation.mutate(failed.change)} /> : null}
      {waiting ? (
        <p role="status" className="welcome-waiting">
          {t.onTheirWay}
        </p>
      ) : null}
      {courses.isSuccess && courses.data.courses.length === 0 && !waiting ? <p>{c.empty}</p> : null}

      {courses.isSuccess && courses.data.courses.length > 0 ? (
        <ul className="welcome-courses">
          {courses.data.courses.map((course) => {
            const id = `welcome-course-${course.moodleCourseId}`;
            return (
              <li key={course.moodleCourseId} className="welcome-course" data-course={course.colour}>
                <label className="welcome-course-head">
                  <input
                    type="checkbox"
                    checked={course.selected}
                    aria-label={`${c.shown}: ${course.code} ${courseTitle(course, locale)}`}
                    onChange={(event) => change(course, { selected: event.target.checked })}
                  />
                  <span className="welcome-course-chip" aria-hidden="true">
                    <span className="welcome-dot" />
                    <bdi dir="ltr">{course.code}</bdi>
                  </span>
                  <strong dir="auto">{courseShortName(course, locale)}</strong>
                </label>
                <span className="welcome-course-foot">
                  <span className="welcome-course-due">
                    {course.nextDue === null ? null : (
                      <>
                        <span aria-hidden="true">📅 </span>
                        {c.nextDue} <bdi dir="auto">{course.nextDue.exerciseTitle}</bdi> &middot;{' '}
                        {formatDue(course.nextDue.dueAt, locale)}
                      </>
                    )}
                  </span>
                  <fieldset className="welcome-swatches">
                    <legend className="welcome-legend">{c.colourFor(course.code)}</legend>
                    {COURSE_PALETTE.map((colour) => (
                      <label key={colour} className="welcome-swatch" data-course={colour} data-checked={course.colour === colour}>
                        <input
                          type="radio"
                          name={`${id}-colour`}
                          value={colour}
                          checked={course.colour === colour}
                          aria-label={c[colour]}
                          onChange={() => change(course, { colour })}
                        />
                        <span aria-hidden="true" />
                      </label>
                    ))}
                  </fieldset>
                </span>
              </li>
            );
          })}
        </ul>
      ) : null}

      <p className="welcome-hint">{t.hint}</p>
      <Link className="welcome-cta" to="/">
        {t.cta}
      </Link>
    </AuthStage>
  );
}
